import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { AuthLayout } from '../components/AuthLayout';
import { Seo } from '../components/Seo';
import { Button, FormError, TextField } from '../components/ui';
import { ApiError } from '../lib/api';
import { IS_NATIVE } from '../lib/base';
import { useAuthConfig } from '../lib/auth/authConfig';
import { executeRecaptcha, preloadRecaptcha } from '../lib/auth/recaptcha';
import { useAuth } from '../lib/auth/AuthProvider';
import { useCurrency } from '../lib/currency';
import { t } from '../lib/i18n';
import { passwordStrength } from '../lib/password';
import { newEventId, trackRegistrationComplete, trackRegistrationStarted } from '../lib/conversion';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Registro de PASO ÚNICO (P2: reducir fricción del tráfico frío). Sólo pedimos
 * email + contraseña + nombre de empresa. El RUBRO se elige después, en el
 * onboarding; el PLAN se difiere (el backend asigna el de entrada para el trial y
 * el usuario lo confirma al final de los 30 días). Sin confirmar-contraseña (el
 * campo tiene ojito mostrar/ocultar) ni nombre/apellido (opcionales, se piden luego).
 */
export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authConfig = useAuthConfig();
  const recaptchaSiteKey = authConfig.data?.recaptcha_site_key ?? '';
  const { currency } = useCurrency();

  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Precargamos el reCAPTCHA + disparamos el evento de inicio del embudo (SPA →
  // hay que llamarlo a mano, no se dispara solo al navegar).
  useEffect(() => {
    if (!IS_NATIVE) preloadRecaptcha(recaptchaSiteKey || undefined);
  }, [recaptchaSiteKey]);
  useEffect(() => {
    trackRegistrationStarted();
    // Precarga desde el flujo de Google (LoginPage redirige con ?email=).
    const qEmail = searchParams.get('email');
    if (qEmail) setEmail(qEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pwStrength = useMemo(() => passwordStrength(password), [password]);

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!companyName.trim()) errs['company.name'] = t('common.required');
    if (!email.trim()) errs['admin.email'] = t('common.required');
    else if (!EMAIL_RE.test(email.trim())) errs['admin.email'] = t('common.emailInvalid');
    if (!pwStrength.ok) errs['admin.password'] = pwStrength.hint || t('register.password.tooShort');
    if (!acceptTerms) errs['accept_terms'] = t('register.terms.error');
    return errs;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError('');
    setEmailTaken(false);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    // event_id compartido navegador↔CAPI (dedup); se genera antes del alta.
    const eventId = newEventId();
    try {
      const captchaToken = IS_NATIVE
        ? undefined
        : await executeRecaptcha(recaptchaSiteKey || undefined, 'register');
      // Sin plan_id (el backend asigna el de entrada) ni nombres (los deriva del
      // email). El rubro se elige en el onboarding.
      await register({
        company: { name: companyName.trim() },
        admin: { email: email.trim(), password },
        captcha_token: captchaToken,
        ref: searchParams.get('ref') || undefined,
        accept_terms: acceptTerms,
      });
      // Conversión: sólo cuando el backend confirmó el alta (no al apretar el botón).
      trackRegistrationComplete({ currency, eventId });
      navigate('/inicio', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'captcha_failed') {
        setTopError(t('auth.captcha.failed'));
      } else if (err instanceof ApiError && err.code === 'email_taken') {
        setEmailTaken(true);
        setFieldErrors((prev) => ({ ...prev, 'admin.email': t('register.error.emailTaken') }));
      } else if (err instanceof ApiError && err.code === 'validation_error' && err.fields) {
        setFieldErrors(err.fields);
      } else {
        setTopError(t('common.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <Seo title={t('seo.register.title')} description={t('seo.register.desc')} />
      <div className="rounded-token border border-line bg-surface p-6 shadow-card sm:p-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="font-display text-2xl font-bold text-ink">{t('register.title')}</h1>
            <p className="text-sm text-muted">{t('register.quickSubtitle')}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sun/15 px-3 py-1 text-xs font-medium text-ink">
            <Sparkles size={14} className="text-sun" />
            {t('register.freeBadge')}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {(topError || emailTaken) && (
            <FormError>
              {topError}
              {emailTaken && (
                <>
                  {' '}
                  <Link to="/login" className="font-medium underline">
                    {t('register.goLogin')}
                  </Link>
                </>
              )}
            </FormError>
          )}

          <TextField
            label={t('register.company.name')}
            name="company_name"
            placeholder={t('register.company.namePlaceholder')}
            autoComplete="organization"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            error={fieldErrors['company.name']}
          />

          <TextField
            label={t('common.email')}
            name="email"
            type="email"
            autoComplete="email"
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors['admin.email']}
          />

          <TextField
            label={t('common.password')}
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder={t('register.admin.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors['admin.password']}
            hint={password ? <StrengthMeter score={pwStrength.score} label={pwStrength.label} /> : undefined}
          />

          <div>
            <label className="flex items-start gap-2.5 text-sm text-muted">
              <input
                type="checkbox"
                name="accept_terms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-line text-brand focus:ring-brand"
              />
              <span>
                {t('register.terms.pre')}{' '}
                <Link
                  to="/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {t('register.terms.tos')}
                </Link>{' '}
                {t('register.terms.and')}{' '}
                <Link
                  to="/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {t('register.terms.privacy')}
                </Link>
                {t('register.terms.post')}
              </span>
            </label>
            {fieldErrors['accept_terms'] && (
              <p className="mt-1 text-xs text-danger">{fieldErrors['accept_terms']}</p>
            )}
          </div>

          <Button type="submit" fullWidth loading={submitting}>
            {submitting ? t('register.submitting') : t('register.submit')}
          </Button>

          <p className="text-center text-xs text-muted">{t('register.quickReassure')}</p>
        </form>

        <p className="mt-6 text-center text-sm text-muted">
          {t('register.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-brand hover:text-brand-2">
            {t('register.goLogin')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function StrengthMeter({ score, label }: { score: number; label: string }) {
  const color = score >= 3 ? 'bg-good' : score === 2 ? 'bg-sun' : 'bg-danger';
  return (
    <span className="flex items-center gap-2">
      <span className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-1 w-6 rounded-full ${i < score ? color : 'bg-line'}`} />
        ))}
      </span>
      <span className="text-muted">{label}</span>
    </span>
  );
}
